import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

export interface BlockInstance {
  id: number;
  type: string;
  area: string;
  position: number;
  props: Record<string, any>;
  // Raw (un-localized) data so the client can re-resolve props on language
  // switch. `settings` = non-translatable; `translations` = per-locale values.
  settings?: Record<string, any>;
  translations?: Record<string, Record<string, any>>;
}

export interface BlockFieldDef {
  name: string;
  type: string;
  label: string;
  default?: any;
  translatable?: boolean;
  options?: Record<string, string>;
  item_fields?: BlockFieldDef[];
  // Conditional visibility — only show this field when the block's settings
  // match every key/value here (value may be a string or list of allowed strings).
  visible_if?: Record<string, string | string[]>;
  // For the `notice` field type — an info box with an optional link.
  text?: string;
  link?: string;
  link_label?: string;
  // For the `listing-picker` field type — which opportunity type to search.
  listing_type?: string;
}

export interface BlockDef {
  key: string;
  label: string;
  icon: string;
  areas: string[];
  category?: string;
  /** One line on what the block puts on the page. From the PHP registry. */
  description?: string;
  /** Reads well on a student's dashboard. A hint for sorting, not a rule. */
  dashboard_pick?: boolean;
  fields: BlockFieldDef[];
}

export interface AppearanceContent {
  announcements: {id: number; text: string; link?: string; bg_color?: string; text_color?: string}[];
  testimonials: {
    id: number;
    name?: string;
    avatar?: string;
    rating?: number;
    role?: string;
    quote?: string;
    quote_type?: 'text' | 'photo' | 'url';
    quote_image?: string;
    quote_url?: string;
  }[];
  galleries: {id: number; slug: string; title?: string; items: {id: number; image: string; link?: string; caption?: string}[]}[];
  faqs: {id: number; question?: string; answer?: string}[];
}

export interface AppearanceData {
  theme: {id: number; slug: string; name: string; options: Record<string, any>} | null;
  blocks: Record<string, BlockInstance[]>;
  registry: BlockDef[];
  areas: string[];
  content: AppearanceContent;
}

/** The `appearance` slice injected into bootstrapData by AppBootstrapData. */
export function getAppearance(): AppearanceData {
  const data = (getBootstrapData() as any)?.appearance;
  return {
    theme: data?.theme ?? null,
    // PHP emits an empty object when there are no blocks; normalise to {}.
    blocks: (data?.blocks && !Array.isArray(data.blocks) ? data.blocks : {}) as Record<string, BlockInstance[]>,
    registry: data?.registry ?? [],
    areas: data?.areas ?? [],
    content: {
      announcements: data?.content?.announcements ?? [],
      testimonials: data?.content?.testimonials ?? [],
      galleries: data?.content?.galleries ?? [],
      faqs: data?.content?.faqs ?? [],
    },
  };
}

/** Active content from the announcement/testimonial/gallery systems. */
export function getAppearanceContent(): AppearanceContent {
  return getAppearance().content;
}

/** A single theme-option value (Theme Options → active theme's options JSON). */
export function getThemeOption<T = any>(name: string, fallback?: T): T {
  const v = getAppearance().theme?.options?.[name];
  return (v === undefined || v === null || v === '' ? fallback : v) as T;
}

/**
 * The site-wide default section background — Theme Options → "Section
 * Backgrounds". `'alternate'` (the default) cycles surfaces by position;
 * `plain`/`tint`/`soft`/`edu` force one surface for every section that hasn't
 * overridden it. Blocks whose own `bg_style` is "Default" (auto) follow this.
 */
export function getSectionBgScheme(): string {
  return getThemeOption<string>('section_bg_scheme', 'alternate');
}

export function getBlocksForArea(area: string): BlockInstance[] {
  const blocks = getAppearance().blocks[area];
  return Array.isArray(blocks) ? [...blocks].sort((a, b) => a.position - b.position) : [];
}
